<div class="module-emails-container">

  <!-- ===== Column 1: Folder Sidebar ===== -->
  <div class="folder-sidebar">
    <!-- Compose Button (shown if user has email accounts) -->
    @if (hasEmailAccounts) {
      <div class="compose-button-container">
        <button mat-raised-button color="primary" class="compose-btn" (click)="openComposeDialog()">
          <mat-icon>edit</mat-icon>
          <span>Compose</span>
        </button>
      </div>
    }

    <!-- Fallback: Folders label (shown if user has no email accounts) -->
    @if (!hasEmailAccounts) {
      <div class="folder-header">Folders</div>
    }

    <div class="folder-list">
      <div class="folder-item"
        [class.active]="activeFolder === 'inbox'"
        (click)="selectFolder('inbox')">
        <span class="material-icons folder-icon">inbox</span>
        <span class="folder-name">Inbox</span>
        @if (inboxCount > 0) {
          <span class="folder-count">{{ inboxCount }}</span>
        }
      </div>
      <div class="folder-item"
        [class.active]="activeFolder === 'outbox'"
        (click)="selectFolder('outbox')">
        <span class="material-icons folder-icon">send</span>
        <span class="folder-name">Sent Items</span>
        @if (sentCount > 0) {
          <span class="folder-count">{{ sentCount }}</span>
        }
      </div>
      <div class="folder-item"
        [class.active]="activeFolder === 'internal_draft'"
        (click)="selectFolder('internal_draft')">
        <span class="material-icons folder-icon">drafts</span>
        <span class="folder-name">Internal Draft</span>
        @if (draftCount > 0) {
          <span class="folder-count">{{ draftCount }}</span>
        }
      </div>
    </div>
  </div>

  <!-- ===== Column 2: Email List ===== -->
  <div class="email-message-list">

    <!-- Search Bar -->
    <div class="list-toolbar">
      <div class="search-box">
        <span class="material-icons search-icon">search</span>
        <input type="text"
          [(ngModel)]="searchText"
          (ngModelChange)="onSearchChange()"
          placeholder="Search emails..."
          class="search-input" />
      </div>
    </div>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-overlay">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    }

    <!-- Empty State -->
    @if (!isLoading && emails.length === 0) {
      <div class="empty-state">
        <mat-icon class="empty-icon">{{ activeFolder === 'inbox' ? 'inbox' : 'send' }}</mat-icon>
        <h4 class="empty-title">No emails</h4>
        <p class="empty-text">There are no {{ activeFolder === 'inbox' ? 'inbox' : 'sent' }} emails linked to this record.</p>
      </div>
    }

    <!-- Email List -->
    @if (!isLoading && emails.length > 0) {
      <div class="email-list-scroll">
        @for (email of emails; track email) {
          <div
            class="email-row"
            [class.unread]="email.type === 'inbox' && !email.is_read"
            [class.selected]="selectedEmail?.id === email.id && selectedEmail?.type === email.type"
            (click)="selectEmail(email)">
            <!-- Email Content -->
            <div class="email-content">
              <!-- Sender Row -->
              <div class="email-sender-row">
                <span class="sender-name" [class.unread]="email.type === 'inbox' && !email.is_read">
                  {{ getSenderDisplay(email) }}
                </span>
                <span class="email-date">{{ formatDate(getEmailDate(email)) }}</span>
              </div>
              <!-- Subject Row -->
              <div class="email-subject-row">
                <span class="subject-text" [class.unread]="email.type === 'inbox' && !email.is_read">
                  {{ email.subject || '(No Subject)' }}
                </span>
                @if (hasAttachments(email)) {
                  <span class="attachment-indicator">
                    <mat-icon>attach_file</mat-icon>
                  </span>
                }
              </div>
              <!-- Preview Row -->
              <div class="email-preview-row">
                <span class="preview-text">{{ getPreviewText(email) }}</span>
              </div>
              <!-- Thread indicator -->
              @if (email.thread_message_count > 1) {
                <div class="email-thread-row">
                  <mat-icon class="thread-icon">forum</mat-icon>
                  <span class="thread-label">{{ email.thread_message_count }} messages</span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- ===== Column 3: Email Detail / Conversation View ===== -->
  <div class="conversation-view">

    <!-- Empty State: No email selected -->
    @if (!selectedEmail) {
      <div class="empty-state">
        <span class="material-icons">mail_outline</span>
        <span>Select an email to read</span>
      </div>
    }

    <!-- Detail Loading -->
    @if (selectedEmail && isDetailLoading) {
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading conversation...</span>
      </div>
    }

    <!-- ===== Thread View (multiple messages) ===== -->
    @if (selectedEmail && threadEmails.length > 0 && !isDetailLoading) {
      <!-- Thread Header -->
      <div class="thread-header">
        <div class="thread-subject">{{ selectedEmail.subject || '(No Subject)' }}</div>
        <div class="thread-meta">
          <span class="message-count">{{ threadEmails.length }} messages</span>
        </div>
      </div>
      <!-- Thread Messages -->
      <div class="thread-messages">
        @for (msg of threadEmails; track msg) {
          <div
            class="thread-message"
            [class.expanded]="isMessageExpanded(msg.id)"
            [class.collapsed]="!isMessageExpanded(msg.id)">
            <!-- Collapsed Message -->
            @if (!isMessageExpanded(msg.id)) {
              <div class="message-collapsed" (click)="toggleMessageExpand(msg.id)">
                <div class="message-avatar" [style.background-color]="getAvatarColor(msg)">
                  <span class="avatar-initials">{{ getAvatarInitials(msg) }}</span>
                </div>
                <div class="message-summary">
                  <span class="sender-name">{{ msg.from_name || msg.from_email || 'Unknown' }}</span>
                  <span class="preview-text">{{ msg.snippet || msg.body_text?.substring(0, 80) }}...</span>
                </div>
                <div class="message-meta">
                  <span class="date">{{ formatDate(msg.received_at || msg.sent_at) }}</span>
                  @if (msg.attachments?.length > 0) {
                    <span class="material-icons attachment-icon">attach_file</span>
                  }
                </div>
              </div>
            }
            <!-- Expanded Message -->
            @if (isMessageExpanded(msg.id)) {
              <div class="message-expanded">
                <div class="message-header">
                  <div class="header-left">
                    <div class="message-avatar large" [style.background-color]="getAvatarColor(msg)">
                      <span class="avatar-initials">{{ getAvatarInitials(msg) }}</span>
                    </div>
                    <div class="sender-info">
                      <div class="sender-name-row">
                        <span class="sender-name">{{ msg.from_name || 'Unknown' }}</span>
                        @if (msg.from_email) {
                          <span class="sender-email">&lt;{{ msg.from_email }}&gt;</span>
                        }
                      </div>
                      @if (msg.to_emails?.length) {
                        <div class="recipients-row">
                          {{ getRecipientsText(msg) }}
                        </div>
                      }
                    </div>
                  </div>
                  <div class="header-right">
                    <span class="date">{{ formatFullDate(msg.received_at || msg.sent_at) }}</span>
                    <div class="header-actions">
                      <button class="action-btn" (click)="replyEmail(msg)" title="Reply">
                        <span class="material-icons">reply</span>
                      </button>
                      <button class="action-btn" (click)="replyAllEmail(msg)" title="Reply All">
                        <span class="material-icons">reply_all</span>
                      </button>
                      <button class="action-btn" (click)="forwardEmail(msg)" title="Forward">
                        <span class="material-icons">forward</span>
                      </button>
                      <button class="action-btn collapse-btn" (click)="toggleMessageExpand(msg.id)" title="Collapse">
                        <span class="material-icons">expand_less</span>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Attachments -->
                @if (msg.attachments?.length > 0) {
                  <div class="email-attachments-section">
                    <div class="attachments-header">
                      <div class="attachments-info">
                        <span class="material-icons">attach_file</span>
                        <span class="attachments-count">{{ msg.attachments.length }} Attachment{{ msg.attachments.length > 1 ? 's' : '' }}</span>
                      </div>
                    </div>
                    <div class="attachments-list">
                      @for (att of msg.attachments; track att) {
                        <a class="attachment-chip" [href]="att.url" target="_blank">
                          <div class="chip-icon">
                            <span class="material-icons">insert_drive_file</span>
                          </div>
                          <span class="chip-name">{{ att.name }}</span>
                          <span class="chip-size">{{ att.formatted_size }}</span>
                        </a>
                      }
                    </div>
                  </div>
                }
                <!-- Message Body -->
                <div class="message-body" [innerHTML]="msg.body_html || msg.body_text"></div>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- ===== Single Email View ===== -->
    @if (selectedEmail && threadEmails.length === 0 && !isDetailLoading) {
      <!-- Header -->
      <div class="thread-header">
        <div class="thread-subject">{{ selectedEmail.subject || '(No Subject)' }}</div>
      </div>
      <div class="thread-messages">
        <div class="thread-message expanded">
          <div class="message-expanded">
            <div class="message-header">
              <div class="header-left">
                <div class="message-avatar large" [style.background-color]="getAvatarColor(selectedEmail)">
                  <span class="avatar-initials">{{ getAvatarInitials(selectedEmail) }}</span>
                </div>
                <div class="sender-info">
                  <div class="sender-name-row">
                    <span class="sender-name">{{ selectedEmail.from_name || 'Unknown' }}</span>
                    @if (selectedEmail.from_email) {
                      <span class="sender-email">&lt;{{ selectedEmail.from_email }}&gt;</span>
                    }
                  </div>
                  <div class="recipients-row">
                    {{ getRecipientsText(selectedEmail) }}
                  </div>
                </div>
              </div>
              <div class="header-right">
                <span class="date">{{ formatFullDate(getEmailDate(selectedEmail)) }}</span>
                <div class="header-actions">
                  <button class="action-btn" (click)="replyEmail(selectedEmail)" title="Reply">
                    <span class="material-icons">reply</span>
                  </button>
                  <button class="action-btn" (click)="replyAllEmail(selectedEmail)" title="Reply All">
                    <span class="material-icons">reply_all</span>
                  </button>
                  <button class="action-btn" (click)="forwardEmail(selectedEmail)" title="Forward">
                    <span class="material-icons">forward</span>
                  </button>
                </div>
              </div>
            </div>
            <!-- Attachments -->
            @if (selectedEmail.attachments?.length > 0) {
              <div class="email-attachments-section">
                <div class="attachments-header">
                  <div class="attachments-info">
                    <span class="material-icons">attach_file</span>
                    <span class="attachments-count">{{ selectedEmail.attachments.length }} Attachment{{ selectedEmail.attachments.length > 1 ? 's' : '' }}</span>
                  </div>
                </div>
                <div class="attachments-list">
                  @for (att of selectedEmail.attachments; track att) {
                    <a class="attachment-chip" [href]="att.url" target="_blank">
                      <div class="chip-icon">
                        <span class="material-icons">insert_drive_file</span>
                      </div>
                      <span class="chip-name">{{ att.name }}</span>
                      <span class="chip-size">{{ att.formatted_size }}</span>
                    </a>
                  }
                </div>
              </div>
            }
            <!-- Message Body -->
            <div class="message-body" [innerHTML]="selectedEmail.body_html || selectedEmail.body_text"></div>
          </div>
        </div>
      </div>
    }

  </div>
</div>
